import type React from 'react';

/**
 * HiddenZoneButton - A clickable invisible button in the bottom-left corner
 *
 * This component renders an invisible button that reveals the chat widget when clicked.
 * Used when the widget is configured with 'hidden_zone_click' display mode.
 *
 * Accessibility:
 * - Keyboard accessible (Enter/Space keys)
 * - ARIA label for screen readers
 * - Positioned in bottom-left corner (16x16px invisible zone)
 */

interface HiddenZoneButtonProps {
    /**
     * Click handler - called when user clicks the hidden zone
     */
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;

    /**
     * Keyboard handler - called when user presses Enter or Space
     */
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;

    /**
     * Optional ref for testing or external access
     */
    buttonRef?: React.RefObject<HTMLButtonElement>;
}

export const HiddenZoneButton: React.FC<HiddenZoneButtonProps> = ({
    onClick,
    onKeyDown,
    buttonRef,
}) => {
    return (
        <button
            type="button"
            ref={buttonRef}
            onClick={onClick}
            onKeyDown={onKeyDown}
            title="Click to show chat widget"
            aria-label="Show chat widget"
            data-testid="hidden-zone-trigger"
            style={{
                position: 'fixed',
                bottom: '0px',
                left: '0px',
                width: '16px',
                height: '16px',
                cursor: 'pointer',
                opacity: 0,
                zIndex: 2147483646, // Just below the widget's z-index
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
            }}
        />
    );
};
